const RESPONSIVE_VIDEO_ATTR = "data-responsive-video-root";
const SLOT_ATTR = "data-responsive-video-slot";
const TEMPLATE_SELECTOR = "template[data-responsive-video-template]";
const BREAKPOINT_ATTR = "data-responsive-video-breakpoint";
const DEFAULT_BREAKPOINT = 719;
const controllers = new WeakMap();

class ResponsiveVideoController {
  constructor(root) {
    this.root = root;
    this.breakpoint = parseInt(root.getAttribute(BREAKPOINT_ATTR), 10);
    if (Number.isNaN(this.breakpoint)) {
      this.breakpoint = DEFAULT_BREAKPOINT;
    }
    this.slots = this.getSlots();
    this.mediaQuery = window.matchMedia
      ? window.matchMedia(`(max-width: ${this.breakpoint}px)`)
      : null;
    this.handleChange = this.handleChange.bind(this);
    this.mountInitial();
  }

  getSlots() {
    const slots = {};
    const slotNodes = this.root.querySelectorAll(`[${SLOT_ATTR}]`);
    slotNodes.forEach((slot) => {
      const type = slot.getAttribute(SLOT_ATTR);
      if (type) {
        slots[type] = slot;
      }
    });
    return slots;
  }

  mountInitial() {
    if (!this.mediaQuery) {
      const preferredType = this.slots.desktop ? "desktop" : "mobile";
      this.switchTo(preferredType);
      return;
    }

    this.updateForMatch(this.mediaQuery.matches);

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener("change", this.handleChange);
    } else if (this.mediaQuery.addListener) {
      this.mediaQuery.addListener(this.handleChange);
    }
  }

  handleChange(event) {
    this.updateForMatch(event.matches);
  }

  updateForMatch(isMobile) {
    const preferred = isMobile ? "mobile" : "desktop";
    if (this.slots[preferred]) {
      this.switchTo(preferred);
      return;
    }

    const fallback = preferred === "mobile" ? "desktop" : "mobile";
    if (this.slots[fallback]) {
      this.switchTo(fallback);
    }
  }

  switchTo(target) {
    if (!target || !this.slots[target]) {
      return;
    }

    this.loadSlot(this.slots[target], target);
    this.setSlotActiveState(this.slots[target], true);

    const other = target === "mobile" ? "desktop" : "mobile";
    if (other !== target) {
      this.setSlotActiveState(this.slots[other], false);
    }

    this.activeType = target;
  }

  loadSlot(slot, type) {
    if (!slot) {
      return;
    }

    if (slot.__responsiveVideoInstance) {
      return;
    }

    const template = slot.querySelector(TEMPLATE_SELECTOR);
    if (!template) {
      return;
    }

    slot.classList.add("animation--lazy-load");
    slot.classList.remove("loaded");

    const fragment = template.content.cloneNode(true);
    const instance = document.createElement("div");
    instance.dataset.responsiveVideoInstance = type;
    instance.appendChild(fragment);
    slot.insertBefore(instance, template);
    slot.__responsiveVideoInstance = instance;

    this.observeVideo(slot);
  }

  setSlotActiveState(slot, isActive) {
    if (!slot) {
      return;
    }

    slot.classList.toggle("responsive-video-slot--active", Boolean(isActive));

    if (isActive) {
      slot.removeAttribute("hidden");
      this.playSlotVideo(slot);
    } else {
      slot.setAttribute("hidden", "hidden");
      this.pauseSlotVideo(slot);
    }
  }

  observeVideo(slot) {
    if (!slot) {
      return;
    }

    const video = slot.querySelector("video");
    if (!video) {
      slot.classList.add("loaded");
      return;
    }

    let isLoaded = false;
    const markLoaded = () => {
      if (isLoaded) {
        return;
      }
      isLoaded = true;
      slot.classList.add("loaded");
    };

    if (video.readyState >= (video.HAVE_CURRENT_DATA || 2)) {
      markLoaded();
      return;
    }

    [
      "loadeddata",
      "canplay",
      "canplaythrough",
      "playing",
      "error",
      "abort",
      "stalled",
    ].forEach((eventName) => {
      video.addEventListener(eventName, markLoaded, { once: true });
    });
  }

  playSlotVideo(slot) {
    const video = slot && slot.querySelector("video");
    if (!video) {
      return;
    }

    video.muted = true;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise.catch(() => {});
    }
  }

  pauseSlotVideo(slot) {
    const video = slot && slot.querySelector("video");
    if (!video) {
      return;
    }
    video.pause();
  }

  destroy() {
    if (this.mediaQuery) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener("change", this.handleChange);
      } else if (this.mediaQuery.removeListener) {
        this.mediaQuery.removeListener(this.handleChange);
      }
    }

    this.unloadSlot(this.slots.mobile);
    this.unloadSlot(this.slots.desktop);
  }

  unloadSlot(slot) {
    if (!slot || !slot.__responsiveVideoInstance) {
      return;
    }
    slot.__responsiveVideoInstance.remove();
    slot.__responsiveVideoInstance = null;
    slot.classList.remove("loaded");
    slot.classList.remove("responsive-video-slot--active");
    slot.setAttribute("hidden", "hidden");
  }
}

const collectRoots = (scope) => {
  const nodes = [];
  if (scope instanceof Element && scope.hasAttribute(RESPONSIVE_VIDEO_ATTR)) {
    nodes.push(scope);
  }
  if (typeof scope.querySelectorAll === "function") {
    scope
      .querySelectorAll(`[${RESPONSIVE_VIDEO_ATTR}]`)
      .forEach((node) => nodes.push(node));
  }
  return nodes;
};

const initResponsiveVideos = (scope) => {
  collectRoots(scope).forEach((root) => {
    if (controllers.has(root)) {
      return;
    }
    controllers.set(root, new ResponsiveVideoController(root));
  });
};

const destroyResponsiveVideos = (scope) => {
  collectRoots(scope).forEach((root) => {
    const controller = controllers.get(root);
    if (!controller) {
      return;
    }
    controller.destroy();
    controllers.delete(root);
  });
};

let isResponsiveVideoBootstrapped = false;

const bootstrapResponsiveVideos = () => {
  if (isResponsiveVideoBootstrapped) {
    return;
  }

  isResponsiveVideoBootstrapped = true;

  const onDOMContentLoaded = () => initResponsiveVideos(document);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onDOMContentLoaded, {
      once: true,
    });
  } else {
    onDOMContentLoaded();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initResponsiveVideos(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyResponsiveVideos(event.target);
  });

  // Reinitialize when blocks are reordered/selected in the editor to ensure previews work.
  ["shopify:block:select", "shopify:block:deselect"].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const detailBlock = event.detail && event.detail.block;
      const target = event.target || detailBlock;
      if (target) {
        initResponsiveVideos(target);
      }
    });
  });
};

const hasResponsiveVideoMarkup = () =>
  Boolean(document.querySelector(`[${RESPONSIVE_VIDEO_ATTR}]`));

if (hasResponsiveVideoMarkup()) {
  bootstrapResponsiveVideos();
}
